import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState('')
  const [resendMessage, setResendMessage] = useState('')
  const [oauthProvider, setOauthProvider] = useState<'google' | 'facebook' | null>(null)
  const { login, resendVerification } = useAuth()
  const navigate = useNavigate()

  // window.location.href navigation isn't instant — a double-tap (easy to do on mobile while
  // waiting for the account picker) can fire two overlapping OAuth authorize requests before
  // the page unloads, which Google's server sometimes rejects with a generic 400. Guard by
  // disabling both buttons after the first click.
  const handleOAuthLogin = (provider: 'google' | 'facebook') => {
    if (oauthProvider) return
    setOauthProvider(provider)
    window.location.href = `${import.meta.env.VITE_API_URL || '/api/v1'}/auth/${provider}`
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setResendMessage('')
    setVerificationEmail('')
    setIsLoading(true)

    const result = await login(formData.email, formData.password)

    setIsLoading(false)

    if (result.success) {
      navigate('/dashboard')
    } else {
      setError(result.error || 'Login failed')
      if (result.requiresVerification && result.email) {
        setVerificationEmail(result.email)
      }
    }
  }

  const handleResend = async () => {
    if (!verificationEmail) return
    setIsLoading(true)
    setResendMessage('')
    const result = await resendVerification(verificationEmail)
    setIsLoading(false)
    if (result.success) {
      setResendMessage('Verification email sent. Please check your inbox.')
    } else {
      setResendMessage(result.error || 'Failed to resend verification email.')
    }
  }

  return (
    <>
      <style>{`
        @media (max-width: 640px) {
          #social-buttons {
            grid-template-columns: 1fr !important;
          }
          .mobile-stack {
            flex-direction: column !important;
          }
        }
      `}</style>
      <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', '--bg-primary': '#F7FAF7', '--forest-green': '#1B5E20', '--emerald': '#2E7D32', '--bg-tint': '#E8F5E9', '--slate': '#4A5568', '--slate-light': '#718096', '--border': '#E2E8F0', '--text-primary': '#1A202C', '--text-secondary': '#4A5568', '--text-muted': '#A0AEC0', '--error-bg': '#FED7D7', '--error-text': '#C53030', '--error-border': '#FC8181', '--success-bg': '#C6F6D5', '--success-text': '#276749', '--success-border': '#9AE6B4'} as React.CSSProperties}>
      {/* Header - Matching Main App Navbar */}
      <header style={{
        background: '#fff',
        borderBottom: '1px solid var(--border)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '1rem 0'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 clamp(1rem, 4vw, 2rem)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Link to="/" style={{display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none'}}>
            <img src="/IGotThat_logo_bk.svg" alt="I Got That U.S." width={230} height={34} style={{width: '230px', height: 'auto', display: 'block'}} />
          </Link>
          <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
            <p style={{
              fontSize: 'clamp(0.8rem, 2vw, 0.95rem)',
              color: 'var(--slate)',
              margin: 0
            }}>
              <span style={{display: 'inline'}} className="hidden sm:inline">New here?{' '}</span>
              <Link to="/register" style={{color: 'var(--forest-green)', fontWeight: 500, textDecoration: 'none'}}>
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </header>

      {/* Main Content - Centered with top padding for fixed header */}
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8" style={{paddingTop: 'var(--auth-header-height)', paddingBottom: '80px', background: 'var(--bg-primary)', minHeight: 'calc(100vh - var(--auth-header-height))'}}>
        <div className="w-full max-w-md" style={{width: '100%', maxWidth: '420px', padding: '1rem'}}>
          {/* Card - responsive padding */}
          <div className="bg-white rounded-2xl shadow-lg border border-[var(--border)]" style={{
            padding: 'clamp(1.5rem, 5vw, 2.5rem)',
            margin: '0 auto'
          }}>
            {/* Title */}
            <div className="text-center mb-6">
              <h1 style={{fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.5rem 0', letterSpacing: '-0.02em'}}>
                Welcome back
              </h1>
              <p style={{color: 'var(--slate)', margin: 0}}>
                Sign in to continue
              </p>
            </div>

            {/* Error */}
            {error && (
              <div style={{marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'var(--error-bg)', color: 'var(--error-text)', fontSize: '0.875rem', border: '1px solid var(--error-border)'}}>
                {error}
                {verificationEmail && (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isLoading}
                    style={{
                      marginTop: '0.75rem',
                      display: 'inline-block',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: 'var(--success-text)',
                      fontWeight: 600,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    {isLoading ? 'Sending...' : 'Resend verification email'}
                  </button>
                )}
              </div>
            )}

            {/* Resend success message */}
            {resendMessage && (
              <div style={{marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'var(--success-bg)', color: 'var(--success-text)', fontSize: '0.875rem', border: '1px solid var(--success-border)'}}>
                {resendMessage}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="email" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  style={{width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', transition: 'all 0.2s'}}
                  placeholder="you@example.com"
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" style={{display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.25rem'}}>
                  Password
                </label>
                <div style={{position: 'relative'}}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    style={{width: '100%', padding: '0.75rem 4rem 0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '1rem', transition: 'all 0.2s'}}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    style={{position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.875rem', color: 'var(--slate-light)', background: 'none', border: 'none', cursor: 'pointer'}}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'var(--forest-green)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isLoading ? 0.5 : 1,
                  marginTop: '1rem'
                }}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            {/* Forgot Password */}
            <div style={{textAlign: 'center', marginTop: '1rem'}}>
              <Link to="/forgot-password" style={{fontSize: '0.875rem', color: 'var(--forest-green)', textDecoration: 'none'}}>
                Forgot your password?
              </Link>
            </div>

            {/* Divider */}
            <div style={{display: 'flex', alignItems: 'center', gap: '1rem', margin: '1.5rem 0'}}>
              <div style={{flex: 1, height: '1px', background: 'var(--border)'}} />
              <span style={{fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em'}}>or continue with</span>
              <div style={{flex: 1, height: '1px', background: 'var(--border)'}} />
            </div>

            {/* Social Login */}
            <div style={{
              display: 'grid',
              gap: '0.75rem'
            }} id="social-buttons">
              <button
                type="button"
                onClick={() => handleOAuthLogin('google')}
                disabled={oauthProvider !== null}
                style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.625rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'white', cursor: oauthProvider !== null ? 'not-allowed' : 'pointer', opacity: oauthProvider !== null ? 0.6 : 1, transition: 'all 0.2s'}}
              >
                <svg style={{width: '1.25rem', height: '1.25rem'}} viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span style={{fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)'}}>
                  {oauthProvider === 'google' ? 'Redirecting...' : 'Google'}
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleOAuthLogin('facebook')}
                disabled={oauthProvider !== null}
                style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.625rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'white', cursor: oauthProvider !== null ? 'not-allowed' : 'pointer', opacity: oauthProvider !== null ? 0.6 : 1, transition: 'all 0.2s'}}
              >
                <svg style={{width: '1.25rem', height: '1.25rem'}} fill="#1877F2" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span style={{fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)'}}>
                  {oauthProvider === 'facebook' ? 'Redirecting...' : 'Facebook'}
                </span>
              </button>
            </div>
          </div>

          {/* Footer */}
          <p style={{textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '1.5rem'}}>
            By signing in, you agree to our{' '}
            <Link to="/terms" style={{color: 'var(--forest-green)', textDecoration: 'none'}}>Terms of Use</Link>{' and '}<Link to="/privacy" style={{color: 'var(--forest-green)', textDecoration: 'none'}}>Privacy Policy</Link>
          </p>
        </div>
      </main>
    </div>
  </>
  )
}

export default Login
